import React, { useCallback, useState } from "react";

import styles from "./price-build-up-filter.module.scss";

import {
  Field,
  Filter,
  SearchTypeField,
  Autocomplete,
  DropdownPaginated,
  DateTimePickerV2,
  Button,
} from "components/commons";
import locale from "localization";
import { useApi } from "hooks";
import { getStationsPricing } from "apis";

const SEARCH_TYPE_OPTIONS = [
  { label: locale.stationCode, value: "stationCode" },
  { label: locale.processedBy, value: "createdByEmail" },
];

const PriceBuildUpFilter = ({
  filterState,
  modifyFilter,
  filterCount,
  applyFilter,
  applyClearFilter,
  searchKey,
  modifySearch,
  applySearch,
  applyClearSearch,
  resetFilter,
  exportReport,
  exporting,
}) => {
  const [stationsTotal, setStationsTotal] = useState(999);

  const { request: fetchStations, loading: loadingStations } = useApi({
    api: getStationsPricing,
    handleOwnError: true,
    params: {
      perPage: 10,
      page: 1,
    },
  });

  const stationRequest = useCallback(
    async ({ page, searchKey: sk }) => {
      const res = await fetchStations({ page, perPage: 10, searchKey: sk });
      if (res?.total != null) setStationsTotal(res.total);
      else if (res?.count != null) setStationsTotal(res.count);
      return res;
    },
    [fetchStations],
  );

  const statusOptions = [
    { label: locale.active, value: "ACTIVE" },
    { label: locale.scheduled, value: "SCHEDULED" },
    { label: locale.expired, value: "EXPIRED" },
    { label: locale.cancelled, value: "CANCELLED" },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.filter}>
        <Filter
          close={resetFilter}
          clear={applyClearFilter}
          submit={applyFilter}
          filterCount={filterCount}
        >
          <Field
            className={styles.fieldLabel}
            labelPosition={"left"}
            childrenClassName={styles.fieldContent}
            label={locale.stationName}
          >
            <DropdownPaginated
              isMultiple
              value={filterState?.stationName?.value || []}
              isAll={filterState?.stationName?.isAll || false}
              allSelectedText={locale.allStations}
              selectAllLabel={locale.all}
              request={stationRequest}
              formatRequest={(res) =>
                (res?.stations || res?.data || []).map((s) => ({
                  label: s.name || s.stationName,
                  value: s.name ?? s.value,
                }))
              }
              total={stationsTotal}
              loading={loadingStations}
              placeholder={locale.allStations}
              onChange={(_, { value, isAll }) => {
                modifyFilter({ stationName: { value, isAll } });
              }}
            />
          </Field>
          <Field
            className={styles.fieldLabel}
            labelPosition={"left"}
            childrenClassName={styles.fieldContent}
            label={locale.status}
          >
            <Autocomplete
              multiple
              hasAll
              toggleAll
              {...filterState?.status}
              options={statusOptions}
              onChange={(_, { value, isAll }) => {
                modifyFilter({
                  status: { value, isAll },
                });
              }}
            />
          </Field>
          <Field
            className={styles.fieldLabel}
            labelPosition={"left"}
            label={locale.effectiveDateAndTime}
          >
            <DateTimePickerV2
              value={filterState.effectiveAt}
              onChange={(_, { value }) => {
                modifyFilter({ effectiveAt: value });
              }}
              className={styles.effectiveDateAndTime}
            />
          </Field>
        </Filter>
        <Field className={styles.search}>
          <SearchTypeField
            value={searchKey}
            onEnter={applySearch}
            onClear={applyClearSearch}
            placeholder="Search..."
            searchWithSymbols={false}
            restrict={false}
            onChange={(_, { value }) => {
              modifySearch(value);
            }}
            searchType={filterState?.searchType || "stationCode"}
            searchKeyName={"searchType"}
            searchKeyValue={filterState.searchType}
            searchKeyOptions={SEARCH_TYPE_OPTIONS}
            searchKeyOnChange={(name, { value }) => {
              modifyFilter({ [name]: value });
            }}
          />
        </Field>
        <Field className={styles.downloadButton}>
          <Button
            startIcon={<span className="icon-download" />}
            onClick={exportReport}
            exportButton
            loading={exporting}
          >
            {locale.export}
          </Button>
        </Field>
      </div>
    </div>
  );
};

export default PriceBuildUpFilter;
